import React from 'react'
import { Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatCurrency } from '@/lib/utils'
import { PS_FUNDING_MATRIX } from '@/lib/planDesign'
import NewCompSolvePanel from '@/components/plan/NewCompSolvePanel'
import CrossTestingStrip from '@/components/plan/CrossTestingStrip'

// Stage ③'s profit-sharing card, standard mode (Phase 5, plan §2.3): the
// three-row grammar — FORMULA (one industry-recognized choice), FUNDING
// (rate / budget / maximize per the legality matrix), CONDITIONS (hours +
// last-day, fixed AND). It reads and writes the AUTHORED profitSharing union
// directly. This guided designer is the only profit-sharing editing surface.

const FORMULA_OPTIONS = [
  { value: 'pro_rata', label: 'Pro rata (same % of pay)', hint: 'Every eligible employee receives the same percentage of pay, or a pro-rata share of a budget.' },
  { value: 'flat_dollar', label: 'Flat dollar (same amount)', hint: 'Every eligible employee receives the same dollar amount.' },
  { value: 'integrated', label: 'Integrated (extra above a wage level)', hint: 'A base rate applies to all pay, with a permitted-disparity rate above the integration level (§401(l)).' },
  { value: 'points', label: 'Points (age, service, and pay)', hint: 'A budget is split using points for age, service, and compensation.' },
  { value: 'age_weighted', label: 'Age-weighted', hint: 'The budget is split so projected benefits at retirement are equal, so older participants receive a larger share.' },
  { value: 'new_comparability', label: 'New comparability', hint: 'Set allocation rates by class or participant; modeled cross-testing updates below.' },
]

const FUNDING_LABELS = {
  rate: 'Specified formula',
  budget: 'Budget (dollar pool)',
  maximize: 'Optimize owner allocation',
}

const defaultFormulaFor = (type) => {
  if (type === 'flat_dollar') return { type, amount: 0 }
  if (type === 'integrated') return { type, basePercent: 3, disparityPercent: 3, integrationLevel: { type: 'twb' } }
  if (type === 'points') return { type, perYearOfAge: 1, perYearOfService: 1, perCompUnit: 1, compUnit: 100 }
  if (type === 'age_weighted') return { type, assumptionSet: 'retira-standard-2026-v1', normalRetirementAge: 65 }
  if (type === 'new_comparability') return { type, groupMode: 'grouped', groups: [] }
  return { type: 'pro_rata', percent: 0 }
}

const fundingFor = (type, currentFunding) => {
  const allowed = PS_FUNDING_MATRIX[type] || ['rate']
  if (allowed.includes(currentFunding?.mode)) return currentFunding
  const mode = allowed[0]
  if (mode === 'budget') return { mode: 'budget', amount: currentFunding?.amount ?? 0 }
  if (mode === 'maximize') return { mode: 'maximize', targets: { rule: 'all-owners' } }
  return { mode: 'rate' }
}

function FieldErrors({ diagnostic }) {
  if (!diagnostic || diagnostic.status !== 'requires-review' || !diagnostic.fieldErrors?.length) return null
  return (
    <div className="space-y-1 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      {diagnostic.fieldErrors.map((error) => (
        <p key={`${error.field}-${error.message}`}>
          <span className="font-medium">{error.field}:</span> {error.message}
        </p>
      ))}
    </div>
  )
}

function GroupFormulaRows({ idPrefix, allocationGroups, formulaGroups, memberCounts, gatewayRate = null, onChange, disabled }) {
  const byGroupId = new Map((formulaGroups || []).map((entry) => [entry.groupId, entry]))
  const setGroupFormula = (groupId, groupLabel, next) => {
    const others = (formulaGroups || []).filter((entry) => entry.groupId !== groupId)
    onChange(next ? [...others, { groupId, groupLabel, formula: next }] : others)
  }
  return (
    <div className="space-y-2">
      {allocationGroups.length === 1 && (
        <p className="rounded-md border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground">
          All employees currently use the default allocation class. Assign classes on the Employees step to set separate rates.
        </p>
      )}
      {allocationGroups.map((group) => {
        const entry = byGroupId.get(group.id)
        const method = entry?.formula?.method ?? 'none'
        const value = entry?.formula?.value ?? 0
        const count = memberCounts?.[group.id] ?? 0
        // A rate left over from an earlier design, pointing at a class nobody in
        // this census carries. Shown so the rate is never silently lost, but
        // marked and removable so it cannot be mistaken for a live class.
        const orphaned = group.source === 'plan'
        return (
          <div
            key={group.id}
            className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border p-2.5 ${
              orphaned ? 'border-dashed border-amber-300/70 bg-amber-50/40' : 'bg-background'
            }`}
          >
            <span className="min-w-[9rem] flex-1 text-sm font-medium">
              {group.label}
              {orphaned ? (
                <span className="ml-1.5 text-xs font-normal text-amber-700">
                  not in this employee list
                </span>
              ) : (
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">{count} {count === 1 ? 'person' : 'people'}</span>
              )}
            </span>
            <Select
              value={method}
              onValueChange={(nextMethod) => {
                if (nextMethod === 'none') setGroupFormula(group.id, group.label, null)
                else if (nextMethod === 'fill_415c') setGroupFormula(group.id, group.label, { method: 'fill_415c' })
                else setGroupFormula(group.id, group.label, { method: nextMethod, value: method === nextMethod ? value : 0 })
              }}
              disabled={disabled}
            >
              <SelectTrigger aria-label={`${group.label} formula`} className="h-8 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Excluded</SelectItem>
                <SelectItem value="percent">Percent of pay</SelectItem>
                <SelectItem value="flat">Flat dollar</SelectItem>
                <SelectItem value="fill_415c">Maximum (§415(c))</SelectItem>
              </SelectContent>
            </Select>
            {method === 'percent' && (
              <span className="flex items-center gap-1.5">
                <NumberInput
                  id={`${idPrefix}-${group.id}-percent`}
                  aria-label={`${group.label} percent of pay`}
                  value={value}
                  onValueChange={(next) => setGroupFormula(group.id, group.label, { method: 'percent', value: Math.max(0, Number(next) || 0) })}
                  containerClassName="w-20"
                  className="w-20"
                  min={0}
                  max={100}
                  step="0.25"
                  disabled={disabled}
                />
                <span className="text-sm text-muted-foreground">%</span>
                {gatewayRate > 0 && value < gatewayRate && (
                  <span className="text-xs text-amber-700">below gateway floor {gatewayRate.toFixed(2)}%</span>
                )}
              </span>
            )}
            {method === 'flat' && (
              <span className="flex items-center gap-1.5">
                <span className="text-sm">$</span>
                <NumberInput
                  id={`${idPrefix}-${group.id}-flat`}
                  aria-label={`${group.label} flat dollar amount`}
                  value={value}
                  onValueChange={(next) => setGroupFormula(group.id, group.label, { method: 'flat', value: Math.max(0, Number(next) || 0) })}
                  containerClassName="w-28"
                  className="w-28"
                  min={0}
                  step="100"
                  disabled={disabled}
                />
              </span>
            )}
            {method === 'fill_415c' && (
              <span className="text-xs text-muted-foreground">Tops each member up to the annual-additions limit.</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function PerPersonAmountsGrid({ idPrefix, employees, amounts, onSetAmount, disabled }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        These amounts stay in this browser and are never uploaded or saved into a plan file. Anyone you
        leave blank gets nothing and is flagged for review.
      </p>
      <div className="max-h-72 overflow-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card text-muted-foreground">
            <tr className="border-b">
              <th className="p-2 text-left font-medium">Employee</th>
              <th className="p-2 text-right font-medium">Compensation</th>
              <th className="p-2 text-right font-medium">Profit sharing $</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id} className="border-b last:border-0">
                <td className="p-2">{employee.name || employee.id}</td>
                <td className="p-2 text-right tabular-nums text-muted-foreground">
                  {formatCurrency(Number(employee.compensation) || 0)}
                </td>
                <td className="p-2 text-right">
                  <NumberInput
                    id={`${idPrefix}-pp-${employee.id}`}
                    aria-label={`${employee.name || employee.id} per-person amount`}
                    value={amounts?.[employee.id] ?? ''}
                    onValueChange={(next) => onSetAmount(employee.id, next === '' || next === null ? null : Math.max(0, Number(next) || 0))}
                    containerClassName="ml-auto w-28"
                    className="w-28 text-right"
                    min={0}
                    step="100"
                    placeholder="Not set"
                    emptyValue=""
                    showError={false}
                    disabled={disabled}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ProfitSharingSection({
  idPrefix,
  design,
  plan = null,
  employerProfile = null,
  metrics = null,
  allocationGroups = [],
  memberCounts = {},
  allocationDiagnostics = [],
  crossTesting = null,
  employees = [],
  calculatedEmployees = [],
  perPersonAmounts = {},
  solverTargets = [],
  onUpdateDesign,
  onManageGroups,
  onSetPerPersonAmount,
  onSetSolverTargets,
  onApplySolution,
  disabled = false,
}) {
  const [solveOpen, setSolveOpen] = React.useState(false)
  const formula = design.formula || { type: 'pro_rata', percent: 0 }
  const funding = design.funding || { mode: 'rate' }
  const allowedFunding = PS_FUNDING_MATRIX[formula.type] || ['rate']
  const formulaOptions = FORMULA_OPTIONS
  const activeOption = formulaOptions.find((option) => option.value === formula.type)
  const diagnostic = allocationDiagnostics.find((entry) => entry.status === 'requires-review')
  const canSolve = (calculatedEmployees || []).some((row) => row.isHCE)

  const patchDesign = (patch) => onUpdateDesign({ ...design, ...patch })
  const patchFormula = (patch) => patchDesign({ formula: { ...formula, ...patch } })

  const handleFormulaType = (type) => {
    const nextFormula = type === formula.type ? formula : defaultFormulaFor(type)
    patchDesign({ formula: nextFormula, funding: fundingFor(type, funding) })
  }

  const handleFundingMode = (mode) => {
    if (!allowedFunding.includes(mode)) return
    if (mode === 'budget') {
      patchDesign({ funding: { mode: 'budget', amount: funding.amount ?? 0 } })
      return
    }
    if (mode === 'maximize') {
      patchDesign({ funding: { mode: 'maximize', targets: funding.targets ?? { rule: 'all-owners' } } })
      if (canSolve) setSolveOpen(true)
      return
    }
    // Budget-funded pro rata drops its percent (§2.3), so switching back to a
    // rate has to restore it in the SAME patch. Two patchDesign calls would each
    // spread the stale design and the second would revert the funding switch,
    // which made this select a one-way door.
    patchDesign({
      funding: { mode: 'rate' },
      ...(formula.type === 'pro_rata' && formula.percent === undefined
        ? { formula: { ...formula, percent: 0 } }
        : {}),
    })
  }

  const hceRows = (calculatedEmployees || []).filter((row) => row.isFivePercentOwner || row.isHCE)

  return (
    <div className="space-y-3 rounded-md border border-border/70 bg-muted/20 p-3" data-testid="ps-standard-editor">
      {/* Row 1: FORMULA */}
      <div className="space-y-2.5">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-ps-formula`} className="text-xs font-medium text-muted-foreground">Formula</Label>
          <Select value={formula.type} onValueChange={handleFormulaType} disabled={disabled}>
            <SelectTrigger id={`${idPrefix}-ps-formula`} aria-label="Profit sharing formula" className="w-full max-w-xl bg-background"><SelectValue placeholder="Choose a formula" /></SelectTrigger>
            <SelectContent>
              {formulaOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {activeOption && <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">{activeOption.hint}</p>}

        {formula.type === 'pro_rata' && funding.mode === 'rate' && (
          <div className="flex items-center gap-1.5">
            <NumberInput
              id={`${idPrefix}-ps-percent`}
              aria-label="Pro rata percent of pay"
              value={formula.percent ?? 0}
              onValueChange={(value) => patchFormula({ percent: Math.max(0, Number(value) || 0) })}
              containerClassName="w-24"
              className="w-24"
              min={0}
              max={100}
              step="0.25"
              disabled={disabled}
            />
            <span className="text-sm text-muted-foreground">% of pay for every eligible employee</span>
          </div>
        )}

        {formula.type === 'flat_dollar' && (
          <div className="flex items-center gap-1.5">
            <span className="text-sm">$</span>
            <NumberInput
              id={`${idPrefix}-ps-flat`}
              aria-label="Flat dollar amount per employee"
              value={formula.amount ?? 0}
              onValueChange={(value) => patchFormula({ amount: Math.max(0, Number(value) || 0) })}
              containerClassName="w-32"
              className="w-32"
              min={0}
              step="100"
              disabled={disabled}
            />
            <span className="text-sm text-muted-foreground">per eligible employee</span>
          </div>
        )}

        {formula.type === 'integrated' && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-ps-base`} className="text-xs text-muted-foreground">Base % (all pay)</Label>
                <NumberInput id={`${idPrefix}-ps-base`} aria-label="Integrated base percent" value={formula.basePercent ?? 0} onValueChange={(value) => patchFormula({ basePercent: Math.max(0, Number(value) || 0) })} containerClassName="w-24" className="w-24" min={0} max={100} step="0.1" disabled={disabled} />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${idPrefix}-ps-disparity`} className="text-xs text-muted-foreground">Disparity % (excess pay)</Label>
                <NumberInput id={`${idPrefix}-ps-disparity`} aria-label="Integrated disparity percent" value={formula.disparityPercent ?? 0} onValueChange={(value) => patchFormula({ disparityPercent: Math.max(0, Number(value) || 0) })} containerClassName="w-24" className="w-24" min={0} max={10} step="0.1" disabled={disabled} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Integration level</Label>
                <Select
                  value={formula.integrationLevel?.type ?? 'twb'}
                  onValueChange={(type) => patchFormula({
                    integrationLevel: type === 'twb' ? { type: 'twb' } : { type, value: formula.integrationLevel?.value ?? 0 },
                  })}
                  disabled={disabled}
                >
                  <SelectTrigger aria-label="Integration level" className="h-9 w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twb">Taxable wage base (TWB)</SelectItem>
                    <SelectItem value="custom_percent_of_twb">Custom % of the TWB</SelectItem>
                    <SelectItem value="custom_dollar">Custom dollar level</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formula.integrationLevel?.type === 'custom_percent_of_twb' && (
                <span className="flex items-center gap-1.5 pb-0.5">
                  <NumberInput aria-label="Integration level percent of TWB" value={formula.integrationLevel?.value ?? 0} onValueChange={(value) => patchFormula({ integrationLevel: { type: 'custom_percent_of_twb', value: Math.max(0, Number(value) || 0) } })} containerClassName="w-24" className="w-24" min={0} max={100} step="1" disabled={disabled} />
                  <span className="text-sm text-muted-foreground">% of TWB</span>
                </span>
              )}
              {formula.integrationLevel?.type === 'custom_dollar' && (
                <span className="flex items-center gap-1.5 pb-0.5">
                  <span className="text-sm">$</span>
                  <NumberInput aria-label="Integration level dollars" value={formula.integrationLevel?.value ?? 0} onValueChange={(value) => patchFormula({ integrationLevel: { type: 'custom_dollar', value: Math.max(0, Number(value) || 0) } })} containerClassName="w-32" className="w-32" min={0} step="100" disabled={disabled} />
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              The disparity rate is capped by §401(l) (5.7% / 5.4% / 4.3%, depending on the integration
              level) and can never exceed the base rate. If a rate breaks those limits you will see an
              error below rather than a quietly corrected number.
            </p>
          </div>
        )}

        {formula.type === 'points' && (
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Points / year of age</Label>
              <NumberInput aria-label="Points per year of age" value={formula.perYearOfAge ?? 0} onValueChange={(value) => patchFormula({ perYearOfAge: Math.max(0, Number(value) || 0) })} containerClassName="w-24" className="w-24" min={0} step="0.5" disabled={disabled} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Points / year of service</Label>
              <NumberInput aria-label="Points per year of service" value={formula.perYearOfService ?? 0} onValueChange={(value) => patchFormula({ perYearOfService: Math.max(0, Number(value) || 0) })} containerClassName="w-24" className="w-24" min={0} step="0.5" disabled={disabled} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Points / comp unit</Label>
              <NumberInput aria-label="Points per compensation unit" value={formula.perCompUnit ?? 0} onValueChange={(value) => patchFormula({ perCompUnit: Math.max(0, Number(value) || 0) })} containerClassName="w-24" className="w-24" min={0} step="0.5" disabled={disabled} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Comp unit ($)</Label>
              <NumberInput aria-label="Compensation unit dollars" value={formula.compUnit ?? 100} onValueChange={(value) => patchFormula({ compUnit: Math.max(1, Number(value) || 100) })} containerClassName="w-24" className="w-24" min={1} step="50" disabled={disabled} />
            </div>
          </div>
        )}

        {formula.type === 'age_weighted' && (
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Normal retirement age</Label>
              <NumberInput aria-label="Normal retirement age" value={formula.normalRetirementAge ?? 65} onValueChange={(value) => patchFormula({ normalRetirementAge: Math.min(100, Math.max(40, Number(value) || 65)) })} containerClassName="w-24" className="w-24" min={40} max={100} integer disabled={disabled} />
            </div>
            <p className="pb-1 text-xs text-muted-foreground">
              Uses standard interest and mortality assumptions, listed with the results. Confirm them
              with the plan&rsquo;s actuary before quoting.
            </p>
          </div>
        )}

        {formula.type === 'new_comparability' && (
          <div className="space-y-2.5">
            {canSolve && (
              <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-background px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">Build an owner-focused design</p>
                  <p className="text-xs text-muted-foreground">Choose the owners, preview the required staff rate, then apply it.</p>
                </div>
                <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={() => setSolveOpen(true)} disabled={disabled}>
                  <Wand2 className="h-3.5 w-3.5" /> Open optimizer
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Set rates by</Label>
                <p className="text-xs text-muted-foreground">Class-based allocation is the standard setup.</p>
              </div>
              <Select
                value={formula.groupMode ?? 'grouped'}
                onValueChange={(groupMode) => patchDesign({
                  formula: groupMode === 'per_person'
                    ? { type: 'new_comparability', groupMode: 'per_person', amountMethod: 'flat' }
                    : { type: 'new_comparability', groupMode: 'grouped', groups: formula.groups ?? [] },
                })}
                disabled={disabled}
              >
                <SelectTrigger aria-label="New comparability rate level" className="h-8 w-56 bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="grouped">Allocation class</SelectItem>
                  <SelectItem value="per_person">Participant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(formula.groupMode ?? 'grouped') === 'grouped' ? (
              <>
                <GroupFormulaRows
                  idPrefix={idPrefix}
                  allocationGroups={allocationGroups}
                  formulaGroups={formula.groups}
                  memberCounts={memberCounts}
                  gatewayRate={crossTesting?.gatewayRate ?? null}
                  onChange={(groups) => patchFormula({ groups })}
                  disabled={disabled}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-2">
                  <span className="text-xs text-muted-foreground">Classes come from the Allocation Class column on Employees.</span>
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onManageGroups} disabled={disabled}>
                    Edit classes on Employees
                  </Button>
                </div>
              </>
            ) : (
              <PerPersonAmountsGrid
                idPrefix={idPrefix}
                employees={employees}
                amounts={perPersonAmounts}
                onSetAmount={onSetPerPersonAmount}
                disabled={disabled}
              />
            )}
            <div className="rounded-md border border-border/60 bg-background/60 p-2.5">
              <CrossTestingStrip crossTesting={crossTesting} employees={employees} />
            </div>
          </div>
        )}
      </div>

      {/* Row 2: FUNDING */}
      <div className="space-y-2 border-t border-border/60 pt-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={`${idPrefix}-ps-funding`} className="text-sm font-medium">Funding</Label>
          <Select value={funding.mode} onValueChange={handleFundingMode} disabled={disabled}>
            <SelectTrigger id={`${idPrefix}-ps-funding`} aria-label="Profit sharing funding" className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(PS_FUNDING_MATRIX[formula.type] || ['rate']).map((mode) => (
                <SelectItem key={mode} value={mode}>{FUNDING_LABELS[mode]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {funding.mode === 'budget' && (
            <span className="flex items-center gap-1.5">
              <span className="text-sm">$</span>
              <NumberInput
                id={`${idPrefix}-ps-budget`}
                aria-label="Budget pool dollars"
                value={funding.amount ?? 0}
                onValueChange={(value) => patchDesign({ funding: { mode: 'budget', amount: Math.max(0, Number(value) || 0) } })}
                containerClassName="w-36"
                className="w-36"
                min={0}
                step="1000"
                disabled={disabled}
              />
            </span>
          )}
        </div>
        {funding.mode === 'budget' && (
          <p className="text-xs text-muted-foreground">
            Anyone who hits the §415(c) or compensation cap keeps their capped amount, and the leftover
            is reported as unallocated rather than pushed onto someone else.
          </p>
        )}
        {funding.mode === 'maximize' && (
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-xs text-muted-foreground">Maximize</Label>
              <Select
                value={funding.targets?.local === true ? 'local' : 'all-owners'}
                onValueChange={(value) => patchDesign({
                  funding: { mode: 'maximize', targets: value === 'local' ? { local: true } : { rule: 'all-owners' } },
                })}
                disabled={disabled}
              >
                <SelectTrigger aria-label="Maximize targets" className="h-8 w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-owners">Every more-than-5% owner</SelectItem>
                  <SelectItem value="local">People I pick (kept on this device)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {funding.targets?.local === true && (
              <div className="flex flex-wrap gap-1.5">
                {hceRows.length === 0 && (
                  <p className="text-xs text-muted-foreground">No HCEs or owners found to target yet.</p>
                )}
                {hceRows.map((row) => {
                  const selected = solverTargets.includes(row.id)
                  return (
                    <button
                      key={row.id}
                      type="button"
                      aria-pressed={selected}
                      disabled={disabled}
                      onClick={() => onSetSolverTargets(
                        selected ? solverTargets.filter((id) => id !== row.id) : [...solverTargets, row.id],
                      )}
                      className={`rounded-md border px-2 py-1 text-xs ${selected ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground'}`}
                    >
                      {row.name || row.id}
                    </button>
                  )
                })}
                {funding.targets?.local === true && solverTargets.length === 0 && (
                  <p className="w-full text-xs text-amber-700">Pick at least one person to maximize, or this design allocates nothing.</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">The target list stays on this device.</p>
              {canSolve && (
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setSolveOpen(true)} disabled={disabled}>
                  <Wand2 className="h-3.5 w-3.5" /> Review optimized design
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={solveOpen && formula.type === 'new_comparability'} onOpenChange={setSolveOpen}>
        <DialogContent className="min-w-0 max-h-[88dvh] max-w-5xl overflow-y-auto" data-testid="new-comp-solve-dialog">
          <DialogHeader>
            <DialogTitle>New comparability design</DialogTitle>
            <DialogDescription>
              Set allocation classes, choose a funding goal, review modeled tests, and apply.
            </DialogDescription>
          </DialogHeader>
          <NewCompSolvePanel
            employees={employees}
            calculatedEmployees={calculatedEmployees}
            plan={plan}
            employerProfile={employerProfile}
            metrics={metrics}
            onApply={onApplySolution}
            onClose={() => setSolveOpen(false)}
            showHeader={false}
          />
        </DialogContent>
      </Dialog>

      <FieldErrors diagnostic={diagnostic} />
    </div>
  )
}
